import * as React from 'react';
import {useTranslation} from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {Drawer, DrawerContent, DrawerDescription, DrawerTitle} from '../../drawer';
import {cn} from '../../lib/cn';

type SelectMenuPanelProps = {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  drawerClassName?: string;
  drawerContentClassName?: string;
  searchVisible?: boolean;
  portal?: boolean;
  anchorRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
};

const MENU_PANEL_CLASSES =
  'z-50 overflow-hidden rounded-lg border border-[var(--chekin-color-gray-3)] bg-white shadow-[0_4px_8px_-2px_rgba(22,22,67,0.06),0_16px_32px_-12px_rgba(33,72,255,0.18)] ring-1 ring-black/[0.02] animate-in fade-in-0 zoom-in-95 duration-150';

function SelectMenuPanel({
  isOpen,
  isMobile,
  onClose,
  title,
  description,
  className,
  drawerClassName,
  drawerContentClassName,
  searchVisible = false,
  portal = false,
  anchorRef,
  children,
}: SelectMenuPanelProps) {
  const {t} = useTranslation();

  if (!isOpen) return null;

  if (isMobile) {
    const fallbackTitle = t('select_option');
    const titleText =
      typeof title === 'string' || typeof title === 'number'
        ? String(title)
        : fallbackTitle;
    const descriptionText =
      typeof description === 'string' || typeof description === 'number'
        ? String(description)
        : titleText;

    const drawerVars = searchVisible
      ? ({'--chekin-drawer-height': 'calc(100dvh - 1rem)'} as React.CSSProperties)
      : undefined;
    const contentVars = searchVisible
      ? ({
          '--chekin-select-menu-max-height': 'calc(100dvh - 9rem)',
        } as React.CSSProperties)
      : undefined;

    return (
      <Drawer
        open={isOpen}
        onOpenChange={next => {
          if (!next) onClose();
        }}
      >
        <DrawerContent
          data-select-menu=""
          onClose={onClose}
          lockScroll={false}
          className={drawerClassName}
          style={drawerVars}
        >
          <DrawerTitle className="sr-only">{titleText}</DrawerTitle>
          <DrawerDescription className="sr-only">{descriptionText}</DrawerDescription>
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col px-1 pb-4 pt-1',
              drawerContentClassName,
            )}
            style={contentVars}
          >
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  if (portal && anchorRef) {
    return (
      <PopoverPrimitive.Root open modal={false}>
        <PopoverPrimitive.Anchor virtualRef={anchorRef} />
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            role="presentation"
            data-select-menu=""
            data-exclude-from-outside-click=""
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={8}
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onEscapeKeyDown={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            className={cn(
              MENU_PANEL_CLASSES,
              'w-[var(--radix-popover-trigger-width)] data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
              className,
            )}
          >
            {children}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }

  return (
    <div
      data-select-menu=""
      className={cn(
        MENU_PANEL_CLASSES,
        'absolute left-0 right-0 top-full mt-1.5 slide-in-from-top-1',
        className,
      )}
    >
      {children}
    </div>
  );
}

export {SelectMenuPanel};
export type {SelectMenuPanelProps};
